import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import EmojiPicker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import { apiService } from "../services/api";
import { Project, User } from "../types/api";
import { sanitizeHtml, htmlToText, isHtmlEmpty } from "../utils/notificationHtml";

// Force color/highlight to serialize as inline styles (e.g. <span style="color:..">)
// rather than CSS classes, so the stored HTML is self-contained and renders the
// same in-app AND in email clients (which strip <style>/class-based CSS).
const ColorStyle = Quill.import("attributors/style/color");
const BackgroundStyle = Quill.import("attributors/style/background");
Quill.register(ColorStyle as Parameters<typeof Quill.register>[0], true);
Quill.register(BackgroundStyle as Parameters<typeof Quill.register>[0], true);

// Toolbar: only inline-style / semantic-tag formats (no class-based ones like
// align) so the emitted HTML stays self-contained for email.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

type Audience = "all_org" | "project_members" | "all_pis" | "specific_users";

interface AdminNotificationCenterProps {
  isOrgAdmin: boolean;
  isPIAnywhere: boolean;
}

interface AudienceOption {
  value: Audience;
  label: string;
  description: string;
  icon: React.ReactNode;
  audienceLevel: 'admin' | 'pi' | 'all';
}

export const AdminNotificationCenter: React.FC<AdminNotificationCenterProps> = ({
  isOrgAdmin,
  isPIAnywhere,
}) => {
  const navigate = useNavigate();
  const canSendAny = isOrgAdmin || isPIAnywhere;

  const [title, setTitle] = useState("");
  const [message, setMessage] = useState(""); // rich HTML from the editor
  const quillRef = useRef<ReactQuill>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [audience, setAudience] = useState<Audience>(
    isOrgAdmin ? "all_org" : "project_members"
  );
  const [projectIds, setProjectIds] = useState<number[]>([]);
  const [userIds, setUserIds] = useState<number[]>([]);

  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState("");

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ recipients: number; recipient_list: Array<{ id: number; name: string; email: string }> } | null>(null);

  const audienceOptions: AudienceOption[] = useMemo(() => [
      {
        value: "all_org",
        label: "Everyone in the organization",
        description: "All active users in your organization",
        audienceLevel: 'admin',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ),
      },
      {
        value: "all_pis",
        label: "All Principal Investigators",
        description: "Every PI in your organization",
        audienceLevel: 'pi',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        ),
      },
      {
        value: "project_members",
        label: "Project members",
        description: "Members of one or more projects you manage",
        audienceLevel: 'all',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
      },
      {
        value: "specific_users",
        label: "Specific users",
        description: "Hand-pick individual recipients",
        audienceLevel: 'all',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      },
    ],
    []
  );

  const visibleAudienceOptions = useMemo(
    () => audienceOptions.filter((opt) => {
      if (opt.audienceLevel === 'all') return true;
      if (opt.audienceLevel === 'admin') return isOrgAdmin;
      if (opt.audienceLevel === 'pi') return isOrgAdmin || isPIAnywhere;
      return false;
    }),
    [audienceOptions, isOrgAdmin, isPIAnywhere]
  );

  useEffect(() => {
    if (!canSendAny) return;
    apiService.adminGetProjects().then(setProjects).catch(() => setProjects([]));
    apiService.adminGetUsers().then(setUsers).catch(() => setUsers([]));
  }, [canSendAny]);

  // Reset audience if it becomes invalid for the current user role
  useEffect(() => {
    const validAudiences = visibleAudienceOptions.map((opt) => opt.value);
    if (!validAudiences.includes(audience)) {
      setAudience(validAudiences[0] as Audience);
    }
  }, [audience, visibleAudienceOptions]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  const toggleProject = (id: number) =>
    setProjectIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  const toggleUser = (id: number) =>
    setUserIds((prev) => (prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]));

  const recipientSummary = useMemo(() => {
    switch (audience) {
      case "all_org":
        return "Everyone in your organization";
      case "all_pis":
        return "All PIs and managers";
      case "project_members":
        return projectIds.length === 0
          ? "Select at least one project"
          : `Members of ${projectIds.length} project${projectIds.length === 1 ? "" : "s"}`;
      case "specific_users":
        return userIds.length === 0
          ? "Select at least one user"
          : `${userIds.length} selected user${userIds.length === 1 ? "" : "s"}`;
    }
  }, [audience, projectIds, userIds]);

  const reset = () => {
    setTitle("");
    setMessage("");
    setProjectIds([]);
    setUserIds([]);
    setUserSearch("");
    setError(null);
  };

  // Insert the chosen emoji as plain unicode at the cursor — renders identically
  // in-app and in email, no per-emoji markup.
  const handleEmojiSelect = (emoji: { native: string }) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, emoji.native, "user");
      editor.setSelection(index + emoji.native.length, 0);
    }
    setShowEmoji(false);
  };

  const handleSend = async () => {
    setError(null);
    setResult(null);

    // Prefer Quill's semantic HTML (clean <ul>/<ol>, inline styles) over the raw
    // editor DOM, then sanitize client-side as a safety net (backend re-sanitizes).
    const editor = quillRef.current?.getEditor();
    const rawHtml = editor ? editor.getSemanticHTML() : message;
    const cleanHtml = sanitizeHtml(rawHtml);

    if (!title.trim() || isHtmlEmpty(cleanHtml)) {
      setError("Title and message are required.");
      return;
    }
    if (audience === "project_members" && projectIds.length === 0) {
      setError("Select at least one project to receive this notification.");
      return;
    }
    if (audience === "specific_users" && userIds.length === 0) {
      setError("Select at least one user to receive this notification.");
      return;
    }

    setSending(true);
    try {
      const res = await apiService.broadcastNotification({
        title: title.trim(),
        message: cleanHtml,
        audience,
        project_ids: audience === "project_members" ? projectIds : undefined,
        user_ids: audience === "specific_users" ? userIds : undefined,
      });
      setResult({ recipients: res.recipients, recipient_list: res.recipient_list || [] });
      reset();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send notification");
    } finally {
      setSending(false);
    }
  };

  if (!canSendAny) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600">You don't have permission to send notifications.</p>
      </div>
    );
  }

  // Post-send confirmation screen replaces the composer entirely so the user
  // doesn't see a stale form. Two clear next actions: leave or compose again.
  if (result !== null) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
            <svg className="w-9 h-9 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Notification sent</h2>
          <p className="mt-2 text-gray-600">
            Delivered to <span className="font-semibold text-gray-900">{result?.recipients}</span>{" "}
            user{result?.recipients === 1 ? "" : "s"}.
          </p>

          {result?.recipient_list && result.recipient_list.length > 0 && (
            <div className="mt-6 max-h-56 overflow-y-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {result.recipient_list.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-900">{user.name}</td>
                      <td className="px-4 py-3 text-gray-600">{user.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 inline-flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="px-5 py-2.5 rounded-md bg-ink text-white text-sm font-semibold hover:bg-ink-800 inline-flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Send another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header — matches NotificationListPage pattern */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900"
          title="Back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">New notification</h1>
          <p className="text-sm text-gray-500 mt-1">
            Send an in-app message to users in your organization.
          </p>
        </div>
      </div>

      {/* Banner alerts */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.34 16a2 2 0 001.73 3z" />
          </svg>
          {error}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
        {/* Title */}
        <div className="p-5">
          <label htmlFor="nc-title" className="block text-sm font-semibold text-gray-800 mb-1.5">
            Title
          </label>
          <input
            id="nc-title"
            type="text"
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Scheduled maintenance on Friday"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-accent focus:ring-1 focus:ring-accent outline-none"
          />
        </div>

        {/* Message — rich text editor (headings, colors, highlight, lists, links,
            emoji). What is composed here is exactly what every recipient sees,
            in-app and by email. */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-gray-800 mb-1.5">
            Message
          </label>
          <div className="relative nc-quill">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={message}
              onChange={setMessage}
              modules={QUILL_MODULES}
              placeholder="Write the notification body…"
            />
            {/* Emoji picker trigger sits just below the editor */}
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowEmoji((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                aria-label="Insert emoji"
              >
                <span aria-hidden>😊</span>
              </button>
              <span className="text-xs text-gray-400">
                {htmlToText(message).length} characters
              </span>
            </div>
            {showEmoji && (
              <>
                {/* click-away backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowEmoji(false)}
                />
                <div className="absolute z-20 mt-1">
                  <EmojiPicker
                    data={emojiData}
                    onEmojiSelect={handleEmojiSelect}
                    previewPosition="none"
                    theme="light"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Audience */}
        <div className="p-5">
          <label className="block text-sm font-semibold text-gray-800 mb-2">Audience</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {visibleAudienceOptions.map((opt) => {
              const active = audience === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={`flex items-start gap-3 text-left rounded-lg border p-3 transition-all ${
                    active
                      ? "border-accent bg-accent-pale ring-1 ring-sand-300"
                      : "border-gray-200 hover:border-sand-300 hover:bg-accent-pale/40"
                  }`}
                >
                  <span
                    className={`flex-shrink-0 mt-0.5 ${
                      active ? "text-accent" : "text-gray-400"
                    }`}
                  >
                    {opt.icon}
                  </span>
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">
                      {opt.label}
                    </span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {opt.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Audience-specific pickers */}
          {audience === "project_members" && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-600 mb-1.5">
                Select projects ({projectIds.length} selected)
              </div>
              <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100 bg-white">
                {projects.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No projects available.</div>
                ) : (
                  projects.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="rounded text-accent focus:ring-accent"
                        checked={projectIds.includes(p.id)}
                        onChange={() => toggleProject(p.id)}
                      />
                      <span className="text-sm text-gray-800">{p.title}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {audience === "specific_users" && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-600 mb-1.5">
                Select users ({userIds.length} selected)
              </div>
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full mb-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-accent focus:ring-1 focus:ring-accent outline-none"
              />
              <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 divide-y divide-gray-100 bg-white">
                {filteredUsers.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">No users match your search.</div>
                ) : (
                  filteredUsers.map((u) => (
                    <label
                      key={u.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="rounded text-accent focus:ring-accent"
                        checked={userIds.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="text-sm text-gray-800 flex-1 truncate">
                        {u.name}
                        <span className="text-gray-500 ml-1">
                          · {u.email}
                          {u.role ? ` · ${u.role}` : ""}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer action bar */}
      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-800">Recipients:</span> {recipientSummary}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="px-5 py-2 rounded-md bg-ink text-white text-sm font-semibold hover:bg-ink-800 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {sending ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Sending…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Send notification
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationCenter;
